/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import React, { createRef, forwardRef, Ref, useCallback, useEffect, useMemo, useState } from 'react';
import { ICON_WARNING } from '../../../../Icons';
import { Chip, CHIP_STATE_INVALID, CHIP_THEME_DEFAULT, CHIP_TYPE_REMOVABLE, ChipProps } from '../../../Chip';
import { SdsIcon } from '../../../SdsIcon';
import { FormField } from '../../FormField';
import { MultiChipFieldProps, multiChipFieldPropsDefaults } from './MultiChipField.props';
import {
  SEPARATOR_TYPE_COMMA,
  SEPARATOR_TYPE_SEMICOLON,
  SEPARATOR_TYPE_SPACE,
  SeparatorType,
} from './MultiChipField.types';

/**
 * SDS MultiChipField component.
 */
export const MultiChipField = forwardRef((props: MultiChipFieldProps, ref: Ref<HTMLInputElement>): JSX.Element => {
  const {
    defaultChips = [],
    callback,
    classes,
    dataId,
    enableFloatingText,
    id,
    emailsRef,
    fieldLabel,
    onItemsChange,
    onRemovableChipClick,
    placeholder,
    transformChipText,
  } = { ...multiChipFieldPropsDefaults, ...props };

  const [active, setActive] = useState<boolean>(false);
  const [chipQueued, setChipQueued] = useState<boolean>(false);
  const [chips, setChips] = useState<Array<ChipProps<string>>>(defaultChips);
  const [inputText, setInputText] = useState<string>('');
  const [remove, setRemove] = useState<string | null>(null);
  const [update, setUpdate] = useState<boolean>(false);

  useEffect(() => {
    if (defaultChips.length) {
      setChips(defaultChips);
    }
  }, [defaultChips]);

  useEffect(() => {
    if (update) {
      onRemovableChipClick?.();
      onItemsChange?.({
        items: chips.map((chip) => chip.text),
        valid: chips.every((chip) => chip.state !== CHIP_STATE_INVALID),
      });
      setUpdate(false);
    }
  }, [chips, onItemsChange, onRemovableChipClick, update]);

  useEffect(() => {
    if (remove) {
      setRemove(null);
      setUpdate(true);
      setChips((chips) => chips.filter((chip) => chip.text !== remove));
    }
  }, [remove]);

  const addChip = useCallback(
    (text: string): void => {
      if (!text) {
        return;
      }
      setInputText('');

      if (transformChipText) {
        text = transformChipText(text);
      }

      // skip duplicates so they don't appear twice
      if (chips.find((chip) => chip.text === text)) {
        return;
      }

      const isInvalid: boolean = callback && !callback(text) ? true : false;

      const newChip: ChipProps<string> = {
        key: text,
        text,
        theme: CHIP_THEME_DEFAULT,
        type: CHIP_TYPE_REMOVABLE,
      };

      if (isInvalid) {
        newChip.state = CHIP_STATE_INVALID;
      }

      setUpdate(true);
      setChips((prev) => [...prev, newChip]);
    },
    [callback, chips, setChips, setUpdate, transformChipText],
  );

  const listClass = clsx(`sds-chip-list`, `sds-multi-chip-list`, { [`sds-multi-chip-list__active`]: active }, classes);

  // Create refs
  const chipRefs = useMemo<Array<React.RefObject<HTMLDivElement>>>(
    () =>
      Array.from(
        {
          length: chips.length,
        },
        () => createRef<HTMLDivElement>(),
      ),
    [chips],
  );

  const getChips = (): Array<JSX.Element> => {
    return chips.map((chip, index): JSX.Element => {
      const classes = clsx(chip.classes, {
        [`sds-chip--${chip.state}`]: chip.state,
      });

      return (
        <Chip
          classes={classes}
          customClick={(): void => {
            setRemove(chip.text);
          }}
          key={chip.key}
          ref={chipRefs[index]}
          text={chip.text}
          theme={chip.theme}
          type={CHIP_TYPE_REMOVABLE}
        />
      );
    });
  };

  const getSeparator = (input: string): SeparatorType => {
    let separator: SeparatorType = SEPARATOR_TYPE_SPACE;
    const string = input.trim();

    if (string.includes(SEPARATOR_TYPE_SEMICOLON)) {
      separator = SEPARATOR_TYPE_SEMICOLON;
    } else if (string.includes(SEPARATOR_TYPE_COMMA)) {
      separator = SEPARATOR_TYPE_COMMA;
    } else {
      separator = SEPARATOR_TYPE_SPACE;
    }
    return separator;
  };

  const onChangeEmailInputForFloatingText = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputText(e.currentTarget.value.trim());
  };

  const onKeyDownEmailInput = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    // Remove last chip
    if (!e.currentTarget.value && e.key === 'Backspace') {
      if (!chips.length) {
        return;
      }

      const reducedChips = chips.slice(0, chips.length - 1);
      setUpdate(true);
      setChips(reducedChips);
      return;
    }

    // Prevent input starting with a space
    if (!e.currentTarget.value && e.key === SEPARATOR_TYPE_SPACE) {
      return e.preventDefault();
    }

    if (
      e.currentTarget.value &&
      [SEPARATOR_TYPE_COMMA, SEPARATOR_TYPE_SEMICOLON, SEPARATOR_TYPE_SPACE, 'Enter'].includes(e.key)
    ) {
      e.preventDefault();
      addChip(e.currentTarget.value.trim());
      return;
    }
  };

  const onPasteEmails = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault();

    const pasteText = event.clipboardData.getData('text');
    if (!pasteText.trim()) {
      return;
    }

    const separator = getSeparator(pasteText);
    const inputList = pasteText.split(separator);

    for (const input of inputList) {
      addChip(input.trim());
    }
  };

  const formFieldProps = { ...props };
  formFieldProps.classes = clsx(`sds-form-input`, classes);

  return (
    <FormField {...formFieldProps}>
      <input
        data-field-emails={chips.map((chip) => chip.text).join(SEPARATOR_TYPE_SEMICOLON)}
        id={id}
        ref={emailsRef}
        type="hidden"
      />
      <ul className={listClass} data-id={dataId}>
        {getChips()}
        <li className="sds-text-field">
          <input
            aria-label={fieldLabel}
            autoComplete="off"
            id={`sds-multi-chip-input__${id}`}
            name="sds-multi-chip-input__chips"
            onBlur={(e): void => {
              // prevent duplicate chip add when floating text is clicked
              if (chipQueued) {
                setChipQueued(false);
                e.target.focus();
                return;
              }

              setActive(false);
              addChip(e.target.value.trim());
            }}
            onChange={onChangeEmailInputForFloatingText}
            onFocus={(): void => setActive(true)}
            onKeyDown={onKeyDownEmailInput}
            onPaste={onPasteEmails}
            placeholder={getChips().length ? '' : placeholder}
            ref={ref}
            type="text"
            value={inputText}
          />
          {!chips.length && props.errorMessage && (
            <div className="sds-field-error__icon">
              <SdsIcon svg={ICON_WARNING} />
            </div>
          )}
        </li>
      </ul>
      {enableFloatingText && inputText && (
        <div
          className="floating-text"
          onMouseDown={(): void => {
            setChipQueued(true);
            addChip(inputText);
          }}
          role="button"
          tabIndex={0}
        >
          {inputText}
        </div>
      )}
    </FormField>
  );
});
